import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, Select } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  InventoryUpdateClient,
  ProductInventoryRuleClient,
  SalesChannelClient,
  type ProductChannelDecisionDTO,
  type SalesChannelDTO,
} from "../../api/inventory";
import {
  BUNDLE_RULE_MODES,
  INVENTORY_RULE_MODES,
  type InventoryRuleChangeSource,
  type InventoryRuleMode,
  type ProductDTO,
  type ProductInventoryRuleDTO,
  type ProductInventoryRuleHistoryDTO,
} from "../../api/product";
import { BundleItemNote, ReasonBadge } from "../inventory/reasons";

/**
 * What this product publishes to each sales channel.
 *
 * One row per channel, always: every product has a rule on every channel.
 * "Listed" off means the channel does not carry the product — it is left out
 * of the files and mutations we send, and answered 0 where the channel asks
 * for its whole catalogue. That is different from BLOCKED, which sends an
 * explicit 0 (Wayfair keeps the last number it was told for a SKU that goes
 * missing, so blocking is how you take a product down there).
 *
 * The daily inventory rule job keeps unlocked rules: the top sellers of each
 * market floor at the channel's default floor, everything else is
 * DISPATCHABLE. Changing a rule here locks it, so the job leaves it alone;
 * unlock it to hand it back. The hard rules hold whatever the lock says, and
 * the server applies them on save: a component is BLOCKED and not listed, a
 * DISCONTINUED or PENDING_LAUNCH product is BLOCKED, and a PENDING_DISCONTINUED
 * product never floors. The controls those rules decide are disabled.
 *
 * Bundles are apart: a bundle's rule is "From its items" (DERIVED) or
 * "Blocked — send 0". From its items, a bundle publishes 0 when one of its
 * single items is blocked on the channel, otherwise what its scarcest single
 * allows (floors included); components are not counted. There is no floor and
 * no lock on a bundle — the daily job never changes a bundle's rule. The
 * "Publishes now" column shows each channel's figure and the item behind it,
 * so nobody has to work it out.
 *
 * Delisting: a DISCONTINUED product keeps going out (as 0 + discontinued) for
 * the channel's `delist_after_sends` successful runs, then its rule is flagged
 * and it leaves the feed. Unchecking the flag here calls the relist endpoint —
 * not the product save, which ignores the two fields — so the SKU goes out
 * another N times and retires itself again.
 */
export function InventoryRulesTab({
  draft,
  onChange,
  readOnly,
}: {
  draft: ProductDTO;
  onChange: (next: ProductDTO) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new SalesChannelClient(http), [http]);
  const ruleClient = useMemo(() => new ProductInventoryRuleClient(http), [http]);

  const { data: channels, isLoading } = useQuery({
    queryKey: ["sales-channel", "all"],
    queryFn: () => client.findAll(),
    staleTime: 30 * 60_000,
  });

  // What each channel receives now, as its feed would decide it. Reflects the
  // saved rules: the product save invalidates ["product", id], which this key
  // sits under.
  const updateClient = useMemo(() => new InventoryUpdateClient(http), [http]);
  const { data: decisions } = useQuery({
    queryKey: ["product", draft.id, "inventory-decisions"],
    queryFn: () => updateClient.productDecisions(draft.id),
    enabled: draft.id > 0,
  });
  const decisionFor = useMemo(() => {
    const byChannel = new Map<number, ProductChannelDecisionDTO>();
    for (const decision of decisions ?? []) byChannel.set(decision.sales_channel_id, decision);
    return byChannel;
  }, [decisions]);

  const hard = hardRules(draft);
  const rules = draft.inventory_rules ?? [];
  const ruleFor = (channel: SalesChannelDTO): ProductInventoryRuleDTO =>
    rules.find((rule) => rule.sales_channel_id === channel.id) ?? {
      // Every pair has a row since 0046; this only covers a product not saved yet.
      id: 0,
      product_id: draft.id,
      sales_channel_id: channel.id,
      mode: hard.bundle ? "DERIVED" : hard.blocked ? "BLOCKED" : "DISPATCHABLE",
      floor_quantity: null,
      is_listed: !hard.component && channel.is_listing_new_products,
      is_locked: false,
    };

  /** Writes one channel's rule into the draft; any edit but the lock itself locks the rule (never on a bundle). */
  const patch = (channel: SalesChannelDTO, change: Partial<ProductInventoryRuleDTO>, lockOnEdit = true) => {
    const lock = lockOnEdit && !hard.bundle;
    const existing = ruleFor(channel);
    // Spread the existing row so the read-only delisting state survives an edit in the draft.
    const next: ProductInventoryRuleDTO = { ...existing, ...change, ...(lock ? { is_locked: true } : {}) };
    if (next.mode !== "FLOOR") next.floor_quantity = null;
    const found = rules.some((rule) => rule.sales_channel_id === channel.id);
    onChange({
      ...draft,
      inventory_rules: found
        ? rules.map((rule) => (rule.sales_channel_id === channel.id ? next : rule))
        : [...rules, next],
    });
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [relisting, setRelisting] = useState<number | null>(null);

  const relist = async (rule: ProductInventoryRuleDTO) => {
    setRelisting(rule.id);
    try {
      const fresh = await ruleClient.relist(rule.id);
      onChange({
        ...draft,
        inventory_rules: rules.map((existing) => (existing.id === rule.id ? { ...existing, ...fresh } : existing)),
      });
      toast.success(t("product.rules.relisted", "Relisted: it goes out again until the channel's threshold"));
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed"));
    } finally {
      setRelisting(null);
    }
  };

  const rows = useMemo<Row[]>(
    () => (channels ?? []).map((channel) => ({ channel, rule: ruleFor(channel) })),
    // ruleFor closes over the current rules; rebuild so the rows track the draft.
    [channels, rules],
  );

  const modeOptions = useMemo(
    () =>
      hard.bundle
        ? BUNDLE_RULE_MODES.map((mode) => ({ value: mode, label: bundleModeLabel(t, mode) }))
        : INVENTORY_RULE_MODES.map((mode) => ({
            value: mode,
            label: modeLabel(t, mode),
            disabled: (hard.blocked && mode !== "BLOCKED") || (hard.noFloor && mode === "FLOOR"),
          })),
    [t, hard.bundle, hard.blocked, hard.noFloor],
  );

  const cols = useMemo(() => {
    const c = defineColumns<Row>();
    return [
      c.custom(
        "channel",
        (row) => row.channel.name,
        (row) => (
          <span className={row.channel.active ? undefined : "text-ink-faint"}>
            {row.channel.name}
            {!row.channel.active && (
              <Badge size="sm" className="ml-2">
                {t("product.rules.channel.inactive", "inactive")}
              </Badge>
            )}
          </span>
        ),
        { headerKey: "product.rules.platform", priority: 1 },
      ),
      c.text("market", (row) => row.channel.market ?? "", {
        headerKey: "product.rules.market",
        filter: "select",
        priority: 3,
      }),
      c.custom(
        "listed",
        (row) => (row.rule.is_listed ? "yes" : "no"),
        (row) => (
          <input
            type="checkbox"
            checked={row.rule.is_listed}
            disabled={readOnly || hard.component}
            title={hard.component ? t("product.rules.hard.component", "Components are never listed") : undefined}
            onChange={(event) => patch(row.channel, { is_listed: event.target.checked })}
            className="size-3.5 accent-[var(--accent)]"
            aria-label={t("product.rules.listed", "Listed")}
          />
        ),
        { headerKey: "product.rules.listed", filter: "select", priority: 1 },
      ),
      c.custom(
        "mode",
        (row) => row.rule.mode,
        (row) => (
          <Select
            value={row.rule.mode}
            options={modeOptions}
            disabled={readOnly || hard.blocked}
            onChange={(event) => {
              const mode = event.target.value as InventoryRuleMode;
              patch(row.channel, {
                mode,
                floor_quantity: mode === "FLOOR" ? row.rule.floor_quantity ?? row.channel.default_floor_quantity ?? 10 : null,
              });
            }}
            className={hard.bundle ? "h-7 w-48" : "h-7 w-40"}
          />
        ),
        { headerKey: "product.rules.mode", filter: "select", priority: 1 },
      ),
      // Bundles have no floor and no lock: their figure comes from their items.
      ...(hard.bundle ? [] : [
      c.custom(
        "floor",
        (row) => (row.rule.mode === "FLOOR" ? row.rule.floor_quantity ?? null : null),
        (row) => {
          if (row.rule.mode !== "FLOOR") return <span className="text-ink-faint"></span>;
          const floor = row.rule.floor_quantity ?? null;
          const invalid = floor === null || floor < 1;
          return (
            <Input
              type="number"
              min={1}
              value={floor ?? ""}
              readOnly={readOnly}
              invalid={invalid}
              title={invalid ? t("product.rules.floor.min", "At least 1") : undefined}
              onChange={(event) =>
                patch(row.channel, { floor_quantity: event.target.value === "" ? null : Number(event.target.value) })
              }
              className="h-7 w-24 text-right"
            />
          );
        },
        { headerKey: "product.rules.floor", filter: "none", align: "right", priority: 2 },
      ),
      c.custom(
        "locked",
        (row) => (row.rule.is_locked ? "locked" : "auto"),
        (row) => (
          <label className="inline-flex items-center gap-2 text-[12px]">
            <input
              type="checkbox"
              checked={row.rule.is_locked}
              disabled={readOnly}
              onChange={(event) => patch(row.channel, { is_locked: event.target.checked }, false)}
              className="size-3.5 accent-[var(--accent)]"
            />
            <span className={row.rule.is_locked ? "text-ink" : "text-ink-muted"}>
              {row.rule.is_locked ? t("product.rules.locked", "Locked") : t("product.rules.auto", "Automatic")}
            </span>
          </label>
        ),
        { headerKey: "product.rules.lock", filter: "select", priority: 2 },
      ),
      ]),
      c.custom(
        "publishes",
        (row) => decisionFor.get(row.channel.id)?.quantity ?? null,
        (row) => <PublishesNow decision={decisionFor.get(row.channel.id)} />,
        { headerKey: "product.rules.publishes", filter: "none", priority: 1 },
      ),
      c.custom(
        "delisted",
        (row) => (row.rule.delisted ? "yes" : `${row.rule.discontinued_sends ?? 0}/${row.channel.delist_after_sends ?? "∞"}`),
        (row) => {
          if (draft.status !== "DISCONTINUED") return <span className="text-ink-faint"></span>;
          if (!row.channel.delist_after_sends) {
            return <span className="text-ink-muted text-[12px]">{t("product.rules.delisted.never", "always sent")}</span>;
          }
          const sends = row.rule.discontinued_sends ?? 0;
          const isDelisted = row.rule.delisted ?? false;
          const label = isDelisted
            ? t("product.rules.delisted.yes", "delisted after {{count}} sends", { count: sends })
            : t("product.rules.delisted.sends", "sent {{sends}}/{{threshold}}", { sends, threshold: row.channel.delist_after_sends });
          return (
            <label className="inline-flex items-center gap-2 text-[12px]">
              <input
                type="checkbox"
                checked={isDelisted}
                // Only unchecking is a user action; the run sets the flag.
                disabled={readOnly || !isDelisted || relisting === row.rule.id || row.rule.id === 0}
                onChange={() => void relist(row.rule)}
                className="size-3.5 accent-[var(--accent)]"
              />
              <span className={isDelisted ? "text-ink" : "text-ink-muted"}>{label}</span>
            </label>
          );
        },
        { headerKey: "product.rules.delisted", filter: "none", priority: 3 },
      ),
    ];
  }, [readOnly, rules, modeOptions, draft.status, relisting, hard.component, hard.blocked, hard.bundle, decisionFor, t]);

  return (
    <>
      {hard.bundle && (
        <div className="mb-3 rounded-md border border-line bg-surface-sunk/60 px-4 py-3 text-[13px] leading-relaxed text-ink-muted">
          <p className="font-medium text-ink">{t("product.rules.bundle.title", "A bundle's stock comes from its single items")}</p>
          <p className="mt-1">
            {t(
              "product.rules.bundle.explain",
              "On each channel the bundle publishes 0 if one of its single items is blocked there; otherwise it publishes what its scarcest single item allows, floors included. Components are not counted. Choose \"Blocked: send 0\" to send 0 whatever the items say. \"Publishes now\" shows each channel's figure and the item behind it.",
            )}
          </p>
        </div>
      )}
      {hard.message && <p className="text-ink-muted mb-2 text-[13px]">{t(hard.message.key, hard.message.fallback)}</p>}
      <DataTable
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.channel.id)}
        loading={isLoading}
        actions={[
          ...(readOnly
            ? []
            : [
                {
                  label: t("product.rules.set-all", "Set every channel"),
                  write: true,
                  onClick: () => setBulkOpen(true),
                },
              ]),
          ...(draft.id
            ? [
                {
                  label: t("product.rules.history", "History"),
                  onClick: () => setHistoryOpen(true),
                },
              ]
            : []),
        ]}
        emptyMessage={t("product.rules.none", "No sales channels configured.")}
      />
      <BulkModeDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        channels={channels ?? []}
        hard={hard}
        onApply={(change) => {
          const next: ProductInventoryRuleDTO[] = (channels ?? []).map((channel) => {
            const existing = ruleFor(channel);
            const mode = change.mode ?? existing.mode;
            return {
              ...existing,
              mode,
              floor_quantity: mode === "FLOOR" ? change.floor ?? existing.floor_quantity ?? null : null,
              is_listed: hard.component ? false : change.listed ?? existing.is_listed,
              is_locked: hard.bundle ? existing.is_locked : change.locked,
            };
          });
          onChange({ ...draft, inventory_rules: next });
          setBulkOpen(false);
        }}
      />
      {historyOpen && (
        <RuleHistoryDialog productId={draft.id} channels={channels ?? []} onClose={() => setHistoryOpen(false)} />
      )}
    </>
  );
}

interface Row {
  channel: SalesChannelDTO;
  rule: ProductInventoryRuleDTO;
}

/** Which hard rules hold for the product, and the line that explains them. Mirrors InventoryRuleAutomation.hard. */
function hardRules(product: ProductDTO): {
  bundle: boolean;
  component: boolean;
  blocked: boolean;
  noFloor: boolean;
  message: { key: string; fallback: string } | null;
} {
  const bundle = product.type === "BUNDLE";
  if (bundle) {
    // A bundle's mode stays editable whatever its status: the status gate
    // publishes 0 for a discontinued or unlaunched bundle on its own.
    let message: { key: string; fallback: string } | null = null;
    if (product.status === "DISCONTINUED") {
      message = { key: "product.rules.bundle.discontinued", fallback: "Discontinued: the bundle publishes 0 on every channel." };
    } else if (product.status === "PENDING_LAUNCH") {
      message = { key: "product.rules.bundle.pending-launch", fallback: "Not launched yet: the bundle publishes 0 on every channel." };
    }
    return { bundle, component: false, blocked: false, noFloor: true, message };
  }
  const component = product.type === "COMPONENT";
  const blocked = component || product.status === "DISCONTINUED" || product.status === "PENDING_LAUNCH";
  const noFloor = product.status === "PENDING_DISCONTINUED";
  let message: { key: string; fallback: string } | null = null;
  if (component) {
    message = { key: "product.rules.hard.component", fallback: "Components are never listed and always blocked." };
  } else if (product.status === "DISCONTINUED") {
    message = { key: "product.rules.hard.discontinued", fallback: "Discontinued: every rule is blocked." };
  } else if (product.status === "PENDING_LAUNCH") {
    message = { key: "product.rules.hard.pending-launch", fallback: "Not launched yet: every rule is blocked." };
  } else if (noFloor) {
    message = { key: "product.rules.hard.pending-discontinued", fallback: "Pending discontinuation: no rule can floor." };
  }
  return { bundle, component, blocked, noFloor, message };
}

export function modeLabel(t: (key: string, fallback: string) => string, mode: InventoryRuleMode): string {
  switch (mode) {
    case "DISPATCHABLE":
      return t("product.rules.mode.dispatchable", "Dispatchable");
    case "FLOOR":
      return t("product.rules.mode.floor", "Floor");
    case "BLOCKED":
      return t("product.rules.mode.blocked", "Blocked");
    case "DERIVED":
      return t("product.rules.mode.derived", "From its items");
  }
}

/** A bundle's two modes, said so nobody has to know what DERIVED means. */
export function bundleModeLabel(t: (key: string, fallback: string) => string, mode: InventoryRuleMode): string {
  return mode === "BLOCKED" ? t("product.rules.mode.bundle-blocked", "Blocked: send 0") : modeLabel(t, mode);
}

/**
 * What one channel receives now: the quantity with its reason and, for a
 * bundle, the single item behind it; or why the channel's file leaves the
 * product out.
 */
function PublishesNow({ decision }: { decision: ProductChannelDecisionDTO | undefined }) {
  const { t } = useTranslation();
  if (!decision) return <span className="text-ink-faint"></span>;
  if (!decision.is_in_feed) {
    return (
      <span className="flex flex-col items-start text-[12px] text-ink-faint">
        <span>{t("product.rules.publishes.not-in-feed", "Not in this feed")}</span>
        {decision.missing_metafield && (
          <span>{t("product.rules.publishes.missing-metafield", "no {{field}}", { field: decision.missing_metafield })}</span>
        )}
      </span>
    );
  }
  return (
    <span className="flex flex-col items-start gap-0.5">
      <span className="flex items-center gap-2">
        <span className="tabular text-[14px] font-semibold text-ink">{decision.quantity}</span>
        <ReasonBadge reason={decision.reason} />
      </span>
      <BundleItemNote reason={decision.reason} detail={decision.detail} />
    </span>
  );
}

const KEEP = "KEEP";

/** One change to every channel at once. Each field can keep what the rules have; the result is locked unless asked otherwise. */
function BulkModeDialog({
  open,
  onClose,
  channels,
  hard,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  channels: SalesChannelDTO[];
  hard: ReturnType<typeof hardRules>;
  onApply: (change: { mode: InventoryRuleMode | null; floor: number | null; listed: boolean | null; locked: boolean }) => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<string>(KEEP);
  const [floor, setFloor] = useState<number | null>(10);
  const [listed, setListed] = useState<string>(KEEP);
  const [locked, setLocked] = useState(true);
  const floorInvalid = mode === "FLOOR" && (floor === null || floor < 1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("product.rules.set-all", "Set every channel")}
      description={t("product.rules.set-all.description", "Applies one change to all {{count}} channels.", {
        count: channels.length,
      })}
      size="sm"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={floorInvalid}
            onClick={() =>
              onApply({
                mode: mode === KEEP ? null : (mode as InventoryRuleMode),
                floor: mode === "FLOOR" ? floor : null,
                listed: listed === KEEP ? null : listed === "yes",
                locked,
              })
            }
          >
            {t("button.apply", "Apply")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("product.rules.mode", "Rule")} htmlFor="bulk-mode">
          <Select
            id="bulk-mode"
            value={mode}
            disabled={hard.blocked}
            options={[
              { value: KEEP, label: t("product.rules.keep", "Keep as is") },
              ...(hard.bundle
                ? BUNDLE_RULE_MODES.map((value) => ({ value, label: bundleModeLabel(t, value) }))
                : INVENTORY_RULE_MODES.map((value) => ({
                    value,
                    label: modeLabel(t, value),
                    disabled: hard.noFloor && value === "FLOOR",
                  }))),
            ]}
            onChange={(event) => setMode(event.target.value)}
          />
        </Field>
        {mode === "FLOOR" && (
          <Field label={t("product.rules.floor", "Floor quantity")} htmlFor="bulk-floor">
            <Input
              id="bulk-floor"
              type="number"
              min={1}
              value={floor ?? ""}
              invalid={floorInvalid}
              onChange={(event) => setFloor(event.target.value === "" ? null : Number(event.target.value))}
            />
          </Field>
        )}
        <Field label={t("product.rules.listed", "Listed")} htmlFor="bulk-listed">
          <Select
            id="bulk-listed"
            value={listed}
            disabled={hard.component}
            options={[
              { value: KEEP, label: t("product.rules.keep", "Keep as is") },
              { value: "yes", label: t("product.rules.listed.yes", "Listed") },
              { value: "no", label: t("product.rules.listed.no", "Not listed") },
            ]}
            onChange={(event) => setListed(event.target.value)}
          />
        </Field>
        {!hard.bundle && (
        <label className="inline-flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={locked}
            onChange={(event) => setLocked(event.target.checked)}
            className="size-3.5 accent-[var(--accent)]"
          />
          {t("product.rules.lock-these", "Lock these rules (the daily job leaves them alone)")}
        </label>
        )}
      </div>
    </Dialog>
  );
}

/** Every change to the product's rules, newest first, from the history the database keeps. */
function RuleHistoryDialog({
  productId,
  channels,
  onClose,
}: {
  productId: number;
  channels: SalesChannelDTO[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const ruleClient = useMemo(() => new ProductInventoryRuleClient(http), [http]);
  const { data, isLoading } = useQuery({
    queryKey: ["product-inventory-rule", "history", productId],
    queryFn: () => ruleClient.history(productId),
  });
  const channelName = useMemo(() => new Map(channels.map((channel) => [channel.id, channel.name])), [channels]);

  const cols = useMemo(() => {
    const c = defineColumns<ProductInventoryRuleHistoryDTO>();
    return [
      c.custom(
        "changed_at",
        (row) => row.changed_at,
        (row) => <span className="tabular-nums">{new Date(row.changed_at).toLocaleString()}</span>,
        { headerKey: "product.rules.history.when", filter: "none", priority: 1 },
      ),
      c.text("channel", (row) => channelName.get(row.sales_channel_id) ?? `#${row.sales_channel_id}`, {
        headerKey: "product.rules.platform",
        filter: "select",
        priority: 1,
      }),
      c.text("change", (row) => describeChange(t, row), { headerKey: "product.rules.history.change", filter: "none", priority: 1 }),
      c.text("source", (row) => sourceLabel(t, row.source), { headerKey: "product.rules.history.source", filter: "select", priority: 2 }),
      c.text("who", (row) => row.account_username ?? "", { headerKey: "product.rules.history.who", filter: "select", priority: 2 }),
    ];
  }, [channelName, t]);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("product.rules.history.title", "Inventory rule history")}
      size="xl"
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t("button.close", "Close")}
        </Button>
      }
    >
      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        emptyMessage={t("product.rules.history.none", "No changes recorded.")}
      />
    </Dialog>
  );
}

function ruleText(
  t: (key: string, fallback: string) => string,
  mode: InventoryRuleMode | null | undefined,
  floor: number | null | undefined,
  listed: boolean | null | undefined,
  locked: boolean | null | undefined,
): string {
  if (!mode) return "";
  const parts = [mode === "FLOOR" ? `${modeLabel(t, mode)} ${floor ?? ""}`.trim() : modeLabel(t, mode)];
  if (listed === false) parts.push(t("product.rules.listed.no", "Not listed"));
  if (locked) parts.push(t("product.rules.locked", "Locked"));
  return parts.join(" · ");
}

function describeChange(t: (key: string, fallback: string) => string, row: ProductInventoryRuleHistoryDTO): string {
  const before = ruleText(t, row.old_mode, row.old_floor_quantity, row.old_is_listed, row.old_is_locked);
  const after = ruleText(t, row.new_mode, row.new_floor_quantity, row.new_is_listed, row.new_is_locked);
  if (row.operation === "INSERT") return after;
  if (row.operation === "DELETE") return `${before} → ${t("product.rules.history.deleted", "deleted")}`;
  if (before === after && row.old_delisted !== row.new_delisted) {
    return row.new_delisted ? t("product.rules.history.delisted", "Delisted") : t("product.rules.history.relisted", "Relisted");
  }
  return `${before} → ${after}`;
}

function sourceLabel(t: (key: string, fallback: string) => string, source: InventoryRuleChangeSource): string {
  switch (source) {
    case "MANUAL":
      return t("product.rules.source.manual", "Product page");
    case "BULK":
      return t("product.rules.source.bulk", "Bulk edit");
    case "IMPORT":
      return t("product.rules.source.import", "Spreadsheet import");
    case "AUTO":
      return t("product.rules.source.auto", "Daily rule job");
    case "SYSTEM":
      return t("product.rules.source.system", "System");
    case "SCRIPT":
      return t("product.rules.source.script", "Operation script");
    case "MIGRATION":
      return t("product.rules.source.migration", "Database migration");
    case "BASELINE":
      return t("product.rules.source.baseline", "Starting state");
    case "SQL":
      return t("product.rules.source.sql", "Database");
  }
}
